import { DropdownItem } from '@callrail/looky/ui';

import { AgencyModel } from '../models/agency.model';
import { CompanyModel } from '../models/company.model';

export const accountIndustries: DropdownItem[] = [
  { id: 'advertising_marketing', name: 'Advertising and Marketing' },
  { id: 'agency', name: 'Agency' },
  { id: 'automotive', name: 'Automotive' },
  { id: 'business_services', name: 'Business Services' },
  { id: 'education', name: 'Education' },
  { id: 'financial_services', name: 'Financial Services' },
  { id: 'healthcare', name: 'Healthcare' },
  { id: 'home_services', name: 'Home Services' },
  { id: 'legal', name: 'Legal' },
  { id: 'manufacturing', name: 'Manufacturing' },
  { id: 'real_estate', name: 'Real Estate' },
  { id: 'real_estate_investor', name: 'Real Estate Investor' },
  { id: 'retail', name: 'Retail' },
  { id: 'software_technology', name: 'Software and Technology' },
  { id: 'travel_hospitality', name: 'Travel and Hospitality' },
  { id: 'other', name: 'Other' },
];

/**
 * Industries that receive personalized integration recommendations.
 * Values must match what the backend stores in agency.industry (display name, not ID).
 */
export const VERTICALIZED_INDUSTRIES = [
  'Advertising and Marketing',
  'Agency',
  'Healthcare',
  'Home Services',
  'Legal',
  'Real Estate',
  'Real Estate Investor',
] as const;

export type VerticalizedIndustry = (typeof VERTICALIZED_INDUSTRIES)[number];

/**
 * The source of truth for verticals: the id stored on a company mapped to the
 * label users see. The ids match the backend IndustryVertical keys exactly.
 * Both the dropdown items and the label lookup below derive from this, so a
 * new vertical only has to be added here. Declaration order is the order the
 * dropdowns render in.
 */
export const ACCOUNT_VERTICAL_NAMES = {
  none: 'None',
  advertising_and_marketing: 'Advertising & Marketing',
  agency: 'Agency',
  automotive: 'Automotive',
  business_services: 'Business Services',
  education: 'Education',
  financial_services: 'Financial Services',
  healthcare: 'Healthcare',
  home_services: 'Home Services',
  legal: 'Legal',
  manufacturing: 'Manufacturing',
  real_estate: 'Real Estate',
  real_estate_investor: 'Real Estate Investor',
  retail: 'Retail',
  software_and_technology: 'Software & Technology',
  travel_and_hospitality: 'Travel & Hospitality',
  other: 'Other',
} as const;

export type AccountVerticalId = keyof typeof ACCOUNT_VERTICAL_NAMES;

export const accountVerticals: DropdownItem[] = Object.entries(
  ACCOUNT_VERTICAL_NAMES
).map(([id, name]) => ({ id, name }));

/**
 * Verticals offered when creating a new account or company. `none` is a valid
 * stored value and stays available in edit/escape-hatch dropdowns, but users
 * must choose a real vertical at creation time — so it is filtered out here.
 */
export const accountVerticalsForCreation: DropdownItem[] =
  accountVerticals.filter((vertical) => vertical.id !== 'none');

/**
 * Translates a stored vertical id into the label users see in the vertical
 * dropdowns. Unrecognized ids pass through unchanged so an unexpected value
 * still says something useful rather than disappearing.
 */
export function verticalDisplayName(verticalId: string): string {
  return ACCOUNT_VERTICAL_NAMES[verticalId as AccountVerticalId] ?? verticalId;
}

export function industryNameToVerticalId(industryName?: string | null): string {
  const verticalId = (industryName ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  return verticalId in ACCOUNT_VERTICAL_NAMES ? verticalId : '';
}

export function resolveVertical(
  company?: Pick<CompanyModel, 'vertical'> | null,
  agency?: Pick<AgencyModel, 'vertical_config' | 'industry'> | null
): string {
  const companyVertical = company?.vertical;
  return (
    (companyVertical && companyVertical !== 'none' ? companyVertical : '') ||
    agency?.vertical_config?.vertical ||
    industryNameToVerticalId(agency?.industry)
  );
}
